import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import SearchIcon from '@mui/icons-material/Search'
import { useNotification } from '../../hooks/useNotification'
import services from '../../services'
import RelatorioHtmlFrame, { ContextoRelatorio, ResultadoConsulta } from '../../components/RelatorioHtmlFrame'
import Janela from '../../novo/componentes/Janela'
import { modeloInicial } from './modeloInicial'
import AbaDados from './AbaDados'
import AbaDisparos from './AbaDisparos'
import AbaSeletorLoja from './AbaSeletorLoja'
import AvisoAusentes from './AvisoAusentes'
import AvisoGuiaNovo, { avisarGuiaBaixado } from './AvisoGuiaNovo'
import ErrosDaPrevia, { useErrosDaPrevia } from './ErrosDaPrevia'
import { ItensAusentes, useItensAusentes } from './ausentes'
import '../../novo/gestao/gestao.scss'
import './relatorioHtml.scss'

interface Achado { nivel: string; mensagem: string }
interface Coluna { nome: string; tipo?: string | null; descricao?: string | null }
interface Medida { nome: string; tabela?: string; descricao?: string | null; formato?: string | null }
interface Dicionario { visao?: string | null; colunas: Coluna[]; medidas: Medida[] }
interface Versao {
  id: number; numero: number; status: string; autorId: number; autorNome?: string
  criadaEm: string; atualizadaEm: string; enviadaEm?: string | null
  revisorNome?: string | null; revisadaEm?: string | null; motivoRecusa?: string | null
  aprovadaEm?: string | null; html?: string | null; achados?: Achado[] | null
}
interface Relatorio {
  dashboardId: number; nome: string; dicionario: Dicionario; versaoPublicadaId?: number | null
  templateId?: number | null
  versoes: Versao[]; emAndamento?: Versao | null; ambientes: { id: number; nome: string }[]
  podeAprovar: boolean
}

const STATUS: Record<string, { rotulo: string; classe: string }> = {
  draft: { rotulo: 'Rascunho', classe: '' },
  in_review: { rotulo: 'Em revisão', classe: 'alerta' },
  rejected: { rotulo: 'Recusada', classe: 'inativo' },
  published: { rotulo: 'Publicada', classe: 'ok' },
  archived: { rotulo: 'Arquivada', classe: '' }
}

const ABAS = ['Relatório', 'Dados', 'Dicionário', 'Versões', 'Disparos', 'Seletor de loja']

const dataHora = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('pt-BR') : '—')

function Situacao({ status }: { status: string }) {
  const s = STATUS[status]
  return <span className={`ng-pill ${s?.classe ?? ''}`}>{s?.rotulo ?? status}</span>
}

/**
 * Autoria e revisão de um relatório HTML, no visual da gestão nova.
 *
 * A prévia roda a versão salva com os dados de verdade e os filtros padrão do ambiente
 * escolhido, mas sem a regra por usuário — quem escreve precisa ver o relatório inteiro.
 * Quem visualiza só recebe a versão publicada, depois de aprovada.
 */
function RelatorioHtml() {
  const { id } = useParams()
  const dashboardId = Number(id)
  const { notify } = useNotification()

  const [relatorio, setRelatorio] = useState<Relatorio | null>(null)
  const [aba, setAba] = useState(0)
  const [html, setHtml] = useState('')
  const [alterado, setAlterado] = useState(false)
  const [achados, setAchados] = useState<Achado[]>([])
  const [ambienteId, setAmbienteId] = useState<number | ''>('')
  // geracao vira a key do iframe: "Recarregar" e a troca de ambiente recarregam mesmo com o HTML igual.
  const [previa, setPrevia] = useState<{ html: string; contexto: ContextoRelatorio; geracao: number } | null>(null)
  const geracaoPrevia = useRef(0)
  const [pendenciasDados, setPendenciasDados] = useState(0)
  // carregar() roda depois de salvar o dicionário ou o template também: não pode apagar código não salvo.
  const alteradoRef = useRef(false)
  useEffect(() => { alteradoRef.current = alterado }, [alterado])
  const [versaoDaPrevia, setVersaoDaPrevia] = useState<number | null>(null)
  const [recusando, setRecusando] = useState(false)
  const [motivo, setMotivo] = useState('')
  const [trabalhando, setTrabalhando] = useState(false)
  const [confirmar, setConfirmar] = useState<{ titulo: string; texto: string; botao: string; perigo?: boolean; acao: () => Promise<void> } | null>(null)

  const emAndamento = relatorio?.emAndamento ?? null

  const carregar = useCallback(async () => {
    const r: any = await services.api.relatorioHtml(dashboardId)
    if (r.fail) {
      notify(r.error, { variant: 'error' })
      return
    }
    const dados: Relatorio = r.data
    setRelatorio(dados)
    if (!alteradoRef.current) {
      setHtml(dados.emAndamento?.html ?? '')
      setAchados(dados.emAndamento?.achados ?? [])
      setAlterado(false)
    }
    setAmbienteId(atual => (atual !== '' ? atual : dados.ambientes[0]?.id ?? ''))
  }, [dashboardId, notify])

  useEffect(() => {
    carregar()
  }, [carregar])

  // A análise roda enquanto a pessoa escreve, com folga entre as teclas: é o que mostra
  // um bloqueio antes de ela tentar enviar.
  useEffect(() => {
    if (!alterado) return
    const relogio = setTimeout(async () => {
      const r: any = await services.api.relatorioHtmlAnalisar(html)
      if (!r.fail) setAchados(r.data)
    }, 600)
    return () => clearTimeout(relogio)
  }, [html, alterado])

  const abrirPrevia = useCallback(async (versaoId: number) => {
    if (ambienteId === '') {
      notify('Este relatório ainda não está em nenhum ambiente. Vincule um grupo para ter prévia com dados.', { variant: 'warning' })
      return
    }
    const r: any = await services.api.relatorioHtmlPrevia(dashboardId, versaoId, ambienteId)
    if (r.fail) {
      notify(r.error, { variant: 'error' })
      return
    }
    setPrevia({ ...r.data, geracao: ++geracaoPrevia.current })
    setVersaoDaPrevia(versaoId)
  }, [dashboardId, ambienteId, notify])

  const consultarPrevia = useCallback(async (consulta: unknown): Promise<ResultadoConsulta> => {
    const r: any = await services.api.relatorioHtmlPreviaDados(dashboardId, ambienteId, consulta)
    if (r.fail) throw new Error(r.error)
    return r.data
  }, [dashboardId, ambienteId])

  // As consultas da prévia que falham ficam juntas no topo dela, com o motivo por extenso.
  const { consultar: consultarPreviaComErros, erros: errosDaPrevia } = useErrosDaPrevia(consultarPrevia, previa?.geracao)
  // O que o dicionário libera e o modelo não tem mais: é o motivo mais comum de bloco dar erro.
  const { ausentes } = useItensAusentes(dashboardId, relatorio?.dicionario)

  // Ao trocar de ambiente, a prévia aberta passa a usar os filtros do novo.
  useEffect(() => {
    if (versaoDaPrevia) abrirPrevia(versaoDaPrevia)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ambienteId])

  const executar = async (acao: () => Promise<any>, sucesso: string) => {
    setTrabalhando(true)
    try {
      const r = await acao()
      if (r?.fail) {
        notify(r.error, { variant: 'error' })
        return null
      }
      notify(sucesso, { variant: 'success' })
      return r?.data ?? true
    } finally {
      setTrabalhando(false)
    }
  }

  const salvar = async (depois?: (versao: Versao) => Promise<void>) => {
    const versao: Versao | null = emAndamento
      ? await executar(() => services.api.relatorioHtmlEditarVersao(emAndamento.id, html), 'Rascunho salvo.')
      : await executar(() => services.api.relatorioHtmlCriarVersao(dashboardId, html), 'Nova versão criada.')
    if (!versao) return
    alteradoRef.current = false
    setAlterado(false)
    await carregar()
    if (depois) await depois(versao)
  }

  const salvarEPrever = () => salvar(v => abrirPrevia(v.id))

  const enviar = async () => {
    if (!emAndamento) return
    if (alterado) {
      notify('Salve as alterações antes de enviar.', { variant: 'warning' })
      return
    }
    if (pendenciasDados > 0) {
      notify('A aba Dados tem mudanças não salvas. Salve ou desfaça lá antes de enviar.', { variant: 'warning' })
      return
    }
    if (await executar(() => services.api.relatorioHtmlAcao(emAndamento.id, 'enviar'), 'Enviado para revisão.')) carregar()
  }

  const aprovar = async () => {
    if (!emAndamento) return
    if (await executar(() => services.api.relatorioHtmlAcao(emAndamento.id, 'aprovar'), 'Versão publicada.')) carregar()
  }

  const recusar = async () => {
    if (!emAndamento) return
    if (await executar(() => services.api.relatorioHtmlAcao(emAndamento.id, 'recusar', { motivo }), 'Versão recusada.')) {
      setRecusando(false)
      setMotivo('')
      carregar()
    }
  }

  const descartar = () => {
    if (!emAndamento) return
    setConfirmar({
      titulo: `Descartar a versão ${emAndamento.numero}`, botao: 'Descartar', perigo: true,
      texto: 'O HTML desta versão se perde. A versão publicada continua no ar.',
      acao: async () => {
        if (await executar(() => services.api.relatorioHtmlDescartarVersao(emAndamento.id), 'Versão descartada.')) {
          setPrevia(null)
          setVersaoDaPrevia(null)
          alteradoRef.current = false
          carregar()
        }
      }
    })
  }

  const republicar = (versao: Versao) => setConfirmar({
    titulo: `Voltar a versão ${versao.numero} para o ar`, botao: 'Voltar para o ar',
    texto: 'A versão publicada hoje vai para o arquivo, e esta passa a ser a que todos veem.',
    acao: async () => {
      if (await executar(() => services.api.relatorioHtmlAcao(versao.id, 'republicar'), `Versão ${versao.numero} de volta ao ar.`)) carregar()
    }
  })

  const usarModelo = () => {
    setHtml(modeloInicial(relatorio?.nome ?? 'Relatório', relatorio?.dicionario ?? { colunas: [], medidas: [] }))
    setAlterado(true)
  }
  const comecarDoModelo = () => {
    if (!html.trim()) { usarModelo(); return }
    setConfirmar({ titulo: 'Começar do modelo', botao: 'Substituir', texto: 'O HTML que está no editor vai ser trocado pelo modelo inicial.', acao: async () => usarModelo() })
  }

  const comecarDaPublicada = async () => {
    if (!relatorio?.versaoPublicadaId) return
    const r: any = await services.api.relatorioHtmlVersao(relatorio.versaoPublicadaId)
    if (r.fail) {
      notify(r.error, { variant: 'error' })
      return
    }
    setHtml(r.data.html ?? '')
    setAlterado(true)
  }

  const bloqueios = achados.filter(a => a.nivel === 'bloqueio')
  const avisos = achados.filter(a => a.nivel === 'aviso')
  const editavel = !emAndamento || emAndamento.status === 'draft' || emAndamento.status === 'rejected'
  const emRevisao = emAndamento?.status === 'in_review'

  if (!relatorio) {
    return (
      <div className="ng">
        <nav className="ng-trilha" aria-label="Caminho"><Link to="/app/gestao/dashboards">Dashboards</Link><span>›</span><span>Relatório HTML</span></nav>
        <p className="ng-explic">Carregando…</p>
      </div>
    )
  }

  const publicada = relatorio.versoes.find(v => v.id === relatorio.versaoPublicadaId)
  const contagem = [null, null, relatorio.dicionario.colunas.length + relatorio.dicionario.medidas.length, relatorio.versoes.length, null, null]

  return (
    <div className="ng">
      <nav className="ng-trilha" aria-label="Caminho">
        <Link to="/app/gestao/dashboards">Dashboards</Link><span>›</span>
        <Link to={`/app/gestao/dashboards/${dashboardId}`}>{relatorio.nome}</Link><span>›</span>
        <span>Relatório HTML</span>
      </nav>

      <div className="ng-cab">
        <h1>
          {relatorio.nome}
          <span className="ng-pill">Relatório HTML</span>
          {publicada ? <span className="ng-pill ok">Versão {publicada.numero} no ar</span> : <span className="ng-pill">Nenhuma versão no ar</span>}
          {emAndamento && <span className={`ng-pill ${STATUS[emAndamento.status]?.classe ?? ''}`}>Versão {emAndamento.numero}: {STATUS[emAndamento.status]?.rotulo ?? emAndamento.status}</span>}
        </h1>
        <div className="ng-acoes">
          <label className="ng-filtros" style={{ fontSize: 13.5, color: 'var(--ng-texto-2)' }}>
            Prévia em
            <select className="ng-select" value={ambienteId} aria-label="Ambiente da prévia"
                    onChange={e => setAmbienteId(e.target.value === '' ? '' : Number(e.target.value))}>
              {relatorio.ambientes.length === 0 && <option value="">Nenhum ambiente</option>}
              {relatorio.ambientes.map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
            </select>
          </label>
        </div>
      </div>

      {/* Guia novo do Claude que a pessoa ainda não baixou. Na aba Disparos o aviso é o do guia de e-mails, dentro da própria aba. */}
      {aba !== 4 && <AvisoGuiaNovo guia="relatorios" />}

      {/* A aba Dados mostra o mesmo aviso com o bloco de cada item; nas outras abas ele fica aqui, sem precisar cavar. */}
      {aba !== 1 && <AvisoAusentes ausentes={ausentes} aoVerDicionario={aba === 2 ? undefined : () => setAba(2)} />}

      <nav className="ng-abas" role="tablist" aria-label="Partes do relatório">
        {ABAS.map((rotulo, i) => (
          <button key={rotulo} type="button" role="tab" aria-selected={aba === i} onClick={() => setAba(i)}>
            {rotulo}{contagem[i] != null && <small>{contagem[i]}</small>}
          </button>
        ))}
      </nav>

      {aba === 0 && (
        <div className="rh-grade">
          <div className="rh-coluna">
            {emAndamento?.status === 'rejected' && emAndamento.motivoRecusa && (
              <div className="rh-alerta erro">Recusada por {emAndamento.revisorNome ?? 'revisor'}: {emAndamento.motivoRecusa}</div>
            )}
            {pendenciasDados > 0 && editavel && (
              <div className="rh-alerta">A aba Dados tem {pendenciasDados === 1 ? '1 mudança não salva' : `${pendenciasDados} mudanças não salvas`}. Elas não entram nesta versão até serem salvas lá.</div>
            )}
            {emRevisao && (
              <div className="rh-alerta">Em revisão desde {dataHora(emAndamento?.enviadaEm)}. Enquanto isso não dá para editar.</div>
            )}

            <div className="rh-botoes">
              <button type="button" className="ng-btn" onClick={comecarDoModelo} disabled={!editavel}>Começar do modelo</button>
              {relatorio.versaoPublicadaId && (
                <button type="button" className="ng-btn" onClick={comecarDaPublicada} disabled={!editavel}>Partir da publicada</button>
              )}
            </div>

            <textarea className="rh-codigo" value={html} disabled={!editavel} spellCheck={false} aria-label="HTML do relatório"
                      onChange={e => { setHtml(e.target.value); setAlterado(true) }}
                      placeholder="Cole aqui o HTML do relatório, ou comece do modelo." />

            {bloqueios.length > 0 && (
              <div className="rh-alerta erro">
                <b>Não dá para enviar enquanto houver:</b>
                <ul>{bloqueios.map((a, i) => <li key={i}>{a.mensagem}</li>)}</ul>
              </div>
            )}
            {avisos.length > 0 && (
              <div className="rh-alerta">
                <b>Vai para o revisor:</b>
                <ul>{avisos.map((a, i) => <li key={i}>{a.mensagem}</li>)}</ul>
              </div>
            )}

            <div className="rh-botoes">
              {editavel && (
                <>
                  <button type="button" className="ng-btn prim" disabled={trabalhando || !html.trim()} onClick={salvarEPrever}>
                    {emAndamento ? 'Salvar e ver prévia' : 'Criar versão e ver prévia'}
                  </button>
                  {emAndamento && (
                    <button type="button" className="ng-btn" disabled={trabalhando || bloqueios.length > 0 || alterado} onClick={enviar}>Enviar para revisão</button>
                  )}
                  {emAndamento && <button type="button" className="ng-btn perigo" disabled={trabalhando} onClick={descartar}>Descartar</button>}
                </>
              )}
              {emRevisao && emAndamento && (
                <>
                  <button type="button" className="ng-btn" onClick={() => abrirPrevia(emAndamento.id)}>Ver prévia</button>
                  {relatorio.podeAprovar && (
                    <>
                      <button type="button" className="ng-btn prim" disabled={trabalhando} onClick={aprovar}>Aprovar e publicar</button>
                      <button type="button" className="ng-btn perigo" disabled={trabalhando} onClick={() => setRecusando(true)}>Recusar</button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          <section className="rh-previa" aria-label="Prévia">
            <div className="rh-previa-cab">
              <span>{previa ? `Prévia da versão ${relatorio.versoes.find(v => v.id === versaoDaPrevia)?.numero ?? ''} · dados reais, sem a regra por pessoa` : 'Prévia'}</span>
              {previa && versaoDaPrevia && <button type="button" className="ng-btn" onClick={() => abrirPrevia(versaoDaPrevia)}>Recarregar</button>}
            </div>
            <ErrosDaPrevia erros={errosDaPrevia} />
            <div className="rh-previa-corpo">
              {previa
                ? <RelatorioHtmlFrame key={previa.geracao} html={previa.html} contexto={previa.contexto} consultar={consultarPreviaComErros} />
                : <div className="rh-vazio">Salve a versão para ver a prévia com os dados do modelo.</div>}
            </div>
          </section>
        </div>
      )}

      {/* Fica montada para não perder as mudanças pendentes ao trocar de aba. */}
      <div style={{ display: aba === 1 ? 'block' : 'none', margin: '0 -24px' }}>
        <AbaDados dashboardId={dashboardId} ambienteId={ambienteId} visivel={aba === 1} dicionario={relatorio.dicionario}
                  emAndamento={emAndamento} versaoPublicadaId={relatorio.versaoPublicadaId}
                  proximoNumero={Math.max(0, ...relatorio.versoes.map(v => v.numero)) + 1}
                  codigoNaoSalvo={alterado} consultar={consultarPrevia} ausentes={ausentes}
                  aoSalvar={async versaoId => { await carregar(); await abrirPrevia(versaoId) }} aoPendencias={setPendenciasDados} />
      </div>

      {aba === 4 && <AbaDisparos dashboardId={dashboardId} dicionario={relatorio.dicionario} />}

      {aba === 5 && <AbaSeletorLoja dashboardId={dashboardId} dicionario={relatorio.dicionario} />}

      {aba === 2 && <EditorDicionario dashboardId={dashboardId} dicionario={relatorio.dicionario} ausentes={ausentes}
                                      templateId={relatorio.templateId ?? null} aoSalvar={carregar} />}

      {aba === 3 && (
        <section className="ng-cartao">
          <div className="ng-tabela">
            <table>
              <thead>
                <tr><th>Versão</th><th>Situação</th><th>Autor</th><th>Enviada</th><th>Revisor</th><th>Aprovada</th><th>Motivo da recusa</th><th /></tr>
              </thead>
              <tbody>
                {relatorio.versoes.map(v => (
                  <tr key={v.id}>
                    <td>{v.numero}</td>
                    <td><Situacao status={v.status} /></td>
                    <td>{v.autorNome ?? `#${v.autorId}`}</td>
                    <td>{dataHora(v.enviadaEm)}</td>
                    <td>{v.revisorNome ?? '—'}</td>
                    <td>{dataHora(v.aprovadaEm)}</td>
                    <td>{v.motivoRecusa ?? '—'}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button type="button" className="ng-btn" onClick={() => { setAba(0); abrirPrevia(v.id) }}>Prévia</button>
                      {relatorio.podeAprovar && v.status === 'archived' && v.aprovadaEm && (
                        <button type="button" className="ng-btn" style={{ marginLeft: 6 }} onClick={() => republicar(v)}>Voltar para o ar</button>
                      )}
                    </td>
                  </tr>
                ))}
                {relatorio.versoes.length === 0 && <tr><td colSpan={8} className="vazio">Nenhuma versão ainda.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <Janela aberto={recusando} titulo={`Recusar a versão ${emAndamento?.numero ?? ''}`} botao="Recusar" perigo
              salvando={trabalhando} desabilitado={motivo.trim().length < 3}
              aoConfirmar={recusar} aoFechar={() => setRecusando(false)}>
        <label className="ng-campo"><span>O que o autor precisa corrigir</span>
          <textarea className="rh-campo-texto" rows={4} autoFocus value={motivo} onChange={e => setMotivo(e.target.value)} />
        </label>
      </Janela>

      <Janela aberto={!!confirmar} titulo={confirmar?.titulo ?? ''} botao={confirmar?.botao ?? ''} perigo={confirmar?.perigo}
              salvando={trabalhando} aoFechar={() => setConfirmar(null)}
              aoConfirmar={async () => { const c = confirmar; setConfirmar(null); if (c) await c.acao() }}>
        <p style={{ margin: 0, lineHeight: 1.5 }}>{confirmar?.texto}</p>
      </Janela>
    </div>
  )
}

/** Baixa um texto como arquivo, sem passar por link do servidor. */
function baixarTexto(nomeArquivo: string, conteudo: string) {
  const url = URL.createObjectURL(new Blob([conteudo], { type: 'text/markdown;charset=utf-8' }))
  const link = document.createElement('a')
  link.href = url
  link.download = nomeArquivo
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/** O template visual do relatório. Salva na hora: não depende do dicionário. */
function EscolhaDeTemplate({ dashboardId, templateId, aoMudar }: { dashboardId: number; templateId: number | null; aoMudar: () => void }) {
  const { notify } = useNotification()
  const [templates, setTemplates] = useState<{ id?: number | null; nome: string; padrao: boolean }[]>([])

  useEffect(() => {
    (async () => {
      const r: any = await services.api.templatesHtml()
      if (!r.fail) setTemplates(r.data)
    })()
  }, [])

  const padrao = templates.find(t => t.padrao)
  const escolher = async (valor: string) => {
    const r: any = await services.api.relatorioHtmlVincularTemplate(dashboardId, valor === '' ? null : Number(valor))
    if (r.fail) notify(r.error, { variant: 'error' })
    else {
      notify('Template do relatório atualizado.', { variant: 'success' })
      aoMudar()
    }
  }

  return (
    <section className="ng-cartao">
      <h2>Template visual</h2>
      <p className="ng-explic">Cores, fonte e componentes do relatório. O portal aplica ao abrir, e o dicionário baixado leva o template para o Claude.</p>
      <div className="rh-botoes">
        <select className="ng-select" aria-label="Template" style={{ minWidth: 280 }} value={templateId == null ? '' : String(templateId)}
                onChange={e => escolher(e.target.value)}>
          <option value="">Padrão{padrao ? ` (${padrao.nome})` : ''}</option>
          {templates.filter(t => t.id != null).map(t => <option key={t.id!} value={String(t.id)}>{t.nome}</option>)}
        </select>
        <Link className="ng-btn" to="/app/gestao/templates">Gerenciar templates</Link>
      </div>
    </section>
  )
}

/**
 * O dicionário: as colunas e medidas que o HTML pode consultar.
 *
 * É também uma regra de acesso — qualquer pessoa que abre o relatório consegue pedir
 * qualquer item daqui, mesmo que o HTML não use. Liberar só o necessário.
 */
function EditorDicionario({ dashboardId, dicionario, templateId, aoSalvar, ausentes }: {
  dashboardId: number; dicionario: Dicionario; templateId: number | null; aoSalvar: () => void; ausentes: ItensAusentes
}) {
  const { notify } = useNotification()
  const [tabelas, setTabelas] = useState<{ nome: string }[]>([])
  const [tabela, setTabela] = useState('')
  const [colunasDaTabela, setColunasDaTabela] = useState<Coluna[]>([])
  const [medidasDoModelo, setMedidasDoModelo] = useState<Medida[]>([])
  const [busca, setBusca] = useState('')
  const [colunas, setColunas] = useState<Coluna[]>(dicionario.colunas)
  const [medidas, setMedidas] = useState<Medida[]>(dicionario.medidas)
  const [visao, setVisao] = useState(dicionario.visao ?? '')
  // O que se baixa é o dicionário salvo; se a tela tem mudança, salva antes.
  const alterado = useMemo(
    () => JSON.stringify({ v: visao.trim(), c: colunas, m: medidas }) !==
      JSON.stringify({ v: (dicionario.visao ?? '').trim(), c: dicionario.colunas, m: dicionario.medidas }),
    [visao, colunas, medidas, dicionario]
  )
  const [erroModelo, setErroModelo] = useState('')

  useEffect(() => {
    (async () => {
      const [t, m]: any[] = await Promise.all([
        services.api.relatorioHtmlModelo(dashboardId, 'tabelas'),
        services.api.relatorioHtmlModelo(dashboardId, 'medidas')
      ])
      if (t.fail || m.fail || !t.data.ok || !m.data.ok) {
        setErroModelo(t.error || m.error || t.data?.erro || m.data?.erro || 'Não foi possível ler o modelo.')
        return
      }
      setTabelas(t.data.itens)
      setMedidasDoModelo(m.data.itens)
    })()
  }, [dashboardId])

  useEffect(() => {
    if (!tabela) return
    (async () => {
      const r: any = await services.api.relatorioHtmlModelo(dashboardId, 'colunas', tabela)
      if (r.fail || !r.data.ok) notify(r.error || r.data?.erro, { variant: 'error' })
      else setColunasDaTabela(r.data.itens)
    })()
  }, [dashboardId, tabela, notify])

  const temColuna = (nome: string) => colunas.some(c => c.nome === nome)
  const temMedida = (nome: string) => medidas.some(m => m.nome === nome)
  const alternarColuna = (c: Coluna) =>
    setColunas(atual => (atual.some(x => x.nome === c.nome) ? atual.filter(x => x.nome !== c.nome) : [...atual, c]))
  const alternarMedida = (m: Medida) =>
    setMedidas(atual => (atual.some(x => x.nome === m.nome) ? atual.filter(x => x.nome !== m.nome) : [...atual, m]))

  // A orientação começa com a descrição do modelo, quando existe, e é o que quem escreve
  // o HTML (gente ou IA) tem para saber o que o campo significa e como usar.
  const descreverColuna = (nome: string, descricao: string) =>
    setColunas(atual => atual.map(c => (c.nome === nome ? { ...c, descricao } : c)))
  const descreverMedida = (nome: string, descricao: string) =>
    setMedidas(atual => atual.map(m => (m.nome === nome ? { ...m, descricao } : m)))
  const semOrientacao = colunas.filter(c => !c.descricao?.trim()).length + medidas.filter(m => !m.descricao?.trim()).length

  const medidasFiltradas = useMemo(
    () => medidasDoModelo.filter(m => m.nome.toLowerCase().includes(busca.toLowerCase())),
    [medidasDoModelo, busca]
  )

  const salvar = async (avisar = true) => {
    const r: any = await services.api.relatorioHtmlSalvarDicionario(dashboardId, { visao, colunas, medidas })
    if (r.fail) {
      notify(r.error, { variant: 'error' })
      return false
    }
    if (avisar) notify('Dicionário salvo.', { variant: 'success' })
    aoSalvar()
    return true
  }

  const baixarDicionario = async () => {
    if (alterado && !(await salvar(false))) return
    const r: any = await services.api.relatorioHtmlExportarDicionario(dashboardId)
    if (r.fail) notify(r.error, { variant: 'error' })
    else baixarTexto(r.data.nomeArquivo, r.data.conteudo)
  }

  const baixarGuia = async () => {
    const r: any = await services.api.relatorioHtmlGuiaClaude()
    if (r.fail) notify(r.error, { variant: 'error' })
    else {
      baixarTexto(r.data.nomeArquivo, r.data.conteudo)
      avisarGuiaBaixado()
    }
  }

  return (
    <>
      <div className="rh-alerta info">
        Só o que estiver aqui pode ser consultado pelo relatório — e qualquer pessoa que abre o relatório consegue pedir
        qualquer item da lista, mesmo que o HTML não use. Libere só o necessário.
      </div>
      {erroModelo && <div className="rh-alerta erro">{erroModelo}</div>}

      <EscolhaDeTemplate dashboardId={dashboardId} templateId={templateId} aoMudar={aoSalvar} />

      <section className="ng-cartao">
        <h2>Visão geral do dashboard</h2>
        <p className="ng-explic">Para que serve, quem usa, que perguntas responde e o que não pode faltar na tela. Vai no topo do dicionário.</p>
        <textarea className="rh-campo-texto" rows={4} maxLength={4000} value={visao} onChange={e => setVisao(e.target.value)}
                  placeholder="Ex.: acompanhamento diário das visitas dos promotores por loja, para supervisores e cliente. Mostrar o % realizado do período, o ranking de lojas críticas e a evolução por dia." />
      </section>

      <div className="rh-dic-grade">
        <section className="ng-cartao">
          <h2>Colunas</h2>
          <select className="ng-select" aria-label="Tabela do modelo" style={{ width: '100%' }} value={tabela} onChange={e => setTabela(e.target.value)}>
            <option value="">Escolha a tabela do modelo</option>
            {tabelas.map(t => <option key={t.nome} value={t.nome}>{t.nome}</option>)}
          </select>
          <div className="rh-lista">
            {colunasDaTabela.map(c => (
              <label key={c.nome}>
                <input type="checkbox" checked={temColuna(c.nome)} onChange={() => alternarColuna(c)} />
                <span>{c.nome}{c.tipo ? <span className="rh-suave">{c.tipo}</span> : null}</span>
              </label>
            ))}
          </div>
        </section>

        <section className="ng-cartao">
          <h2>Medidas</h2>
          <label className="ng-busca" style={{ maxWidth: 'none' }}>
            <SearchIcon fontSize="small" />
            <input placeholder="Buscar medida" value={busca} onChange={e => setBusca(e.target.value)} />
          </label>
          <div className="rh-lista">
            {medidasFiltradas.map(m => (
              <label key={m.nome} title={m.descricao ?? ''}>
                <input type="checkbox" checked={temMedida(m.nome)} onChange={() => alternarMedida(m)} />
                <span>{m.nome}{!m.descricao && <span className="rh-atencao">sem descrição no modelo</span>}</span>
              </label>
            ))}
          </div>
        </section>
      </div>

      <section className="ng-cartao">
        <h2>Liberado para o relatório ({colunas.length + medidas.length})</h2>
        <p className="ng-explic">
          Escreva o que cada item significa e como usar: unidade, período, se soma ou não, cuidados de leitura.
          É isso que orienta quem monta o HTML — inclusive o Claude.
          {semOrientacao > 0 && <span className="rh-atencao">{semOrientacao} sem orientação.</span>}
        </p>
        {colunas.length + medidas.length > 0 && (
          <div className="ng-tabela">
            <table>
              <thead><tr><th style={{ width: 90 }}>Tipo</th><th style={{ width: '32%' }}>Nome</th><th>Orientação</th><th style={{ width: 100 }} /></tr></thead>
              <tbody>
                {colunas.map(c => (
                  <tr key={'c' + c.nome} className={ausentes.colunas.includes(c.nome) ? 'rh-linha-ausente' : undefined}>
                    <td><span className="ng-tag">Coluna</span></td>
                    <td style={{ fontSize: 13.5 }}>
                      {c.nome}{c.tipo ? <span className="rh-suave">{c.tipo}</span> : null}
                      {ausentes.colunas.includes(c.nome) && <span className="rh-ausente">não existe mais no modelo</span>}
                    </td>
                    <td>
                      <textarea className="rh-campo-texto" rows={1} maxLength={1000} value={c.descricao ?? ''}
                                placeholder="Ex.: código da loja no Protheus; use para cruzar com a base de lojas"
                                onChange={e => descreverColuna(c.nome, e.target.value)} />
                    </td>
                    <td><button type="button" className="ng-btn perigo" onClick={() => alternarColuna(c)}>Remover</button></td>
                  </tr>
                ))}
                {medidas.map(m => (
                  <tr key={'m' + m.nome} className={ausentes.medidas.includes(m.nome) ? 'rh-linha-ausente' : undefined}>
                    <td><span className="ng-tag">Medida</span></td>
                    <td style={{ fontSize: 13.5 }}>
                      {m.nome}
                      {ausentes.medidas.includes(m.nome) && <span className="rh-ausente">não existe mais no modelo</span>}
                    </td>
                    <td>
                      <textarea className="rh-campo-texto" rows={1} maxLength={1000} value={m.descricao ?? ''}
                                placeholder="Ex.: visitas realizadas / previstas no período filtrado, em %; não somar entre lojas"
                                onChange={e => descreverMedida(m.nome, e.target.value)} />
                    </td>
                    <td><button type="button" className="ng-btn perigo" onClick={() => alternarMedida(m)}>Remover</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="rh-botoes">
        <button type="button" className="ng-btn prim" onClick={() => salvar()}>Salvar dicionário</button>
        <button type="button" className="ng-btn" onClick={baixarDicionario}>{alterado ? 'Salvar e baixar dicionário' : 'Baixar dicionário'}</button>
        <button type="button" className="ng-btn" onClick={baixarGuia}>Baixar guia padrão</button>
      </div>
      <div className="rh-alerta info">
        Para o Claude: o <b>guia padrão</b> explica o conceito, o SDK e as regras, e é igual para todos os relatórios —
        coloque uma vez nos arquivos do projeto do Claude (e troque quando a versão no nome do arquivo mudar). O{' '}
        <b>dicionário</b> traz só este dashboard: a visão geral, as colunas e as medidas com as orientações. Mande ele a
        cada relatório.
      </div>
    </>
  )
}

export default RelatorioHtml
